import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

import { leadAPI, dsaEnquiryAPI } from '../../services/api';
import { validateEmail, validateMobile } from '../../utils/helpers';

// Helper to get correct API URL
const getApiUrl = (path) => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocal ? `http://localhost:8001${path}` : path;
};

const EnquiryModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP States
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  // Watch mobile field for OTP functionality
  const mobileValue = watch('mobile', '');

  // Handle OTP sending via Karix
  const handleSendOtp = async () => {
    if (!mobileValue) {
      toast.error('Please enter your phone number first');
      return;
    }
    if (!validateMobile(mobileValue)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSendingOtp(true);

    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: mobileValue }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success('OTP sent successfully!');
      } else {
        toast.error(data.message || 'Failed to send OTP.');
      }
    } catch (error) {
      console.error('OTP Send error:', error);
      toast.error('Error sending OTP.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    const verifyToast = toast.loading('Verifying OTP...');

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: mobileValue, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success('OTP verified successfully!', { id: verifyToast });
      } else {
        toast.error(data.message || 'Invalid or expired OTP', { id: verifyToast });
      }
    } catch (error) {
      console.error('OTP Verify error:', error);
      toast.error('An error occurred during verification.', { id: verifyToast });
    }
  };

  const onSubmit = async (data) => {
    if (!otpVerified) {
      toast.error('Please verify your phone number with OTP first');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting your enquiry...');

    const submissionData = {
      name: data.name,
      mobile: data.mobile,
      email: data.email,
      city: data.city,
      applying_as: data.applying_as,
      experience_years: data.experience_years,
      details: data.details
    };

    try {
      const response = await dsaEnquiryAPI.submit(submissionData);

      if (response.data.success) {
        toast.success('Enquiry submitted successfully!', { id: loadingToast });
        navigate('/thank-you');
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
      }
    } catch (error) {
      console.error('Enquiry submission failed:', error);
      toast.error('Failed to submit enquiry. Please try again.', { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setOtpSent(false);
    setOtp('');
    setOtpVerified(false);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 50,
      transition: { duration: 0.2 }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="fixed inset-0 z-[10001] overflow-y-auto"
      >
        {/* Backdrop */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal Content */}
          <motion.div
            variants={contentVariants}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-primary text-white p-6 relative overflow-hidden flex-shrink-0">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-3 rounded-xl">
                  <DocumentTextIcon className="h-8 w-8 text-black" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black/80">DSA enquiry</h3>
                  <p className="text-black/80">Apply to become a DSA partner</p>
                </div>
              </div>

              {/* Background decoration */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
            </div>

            {/* Form */}
            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.name')} *
                    </label>
                    <input
                      {...register('name', {
                        required: t('forms.required'),
                        minLength: { value: 2, message: 'Name must be at least 2 characters' }
                      })}
                      className={`input-primary ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  {/* Mobile with OTP */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.mobile')} *
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        {...register('mobile', {
                          required: t('forms.required'),
                          validate: (value) => validateMobile(value) || t('forms.validMobile'),
                          onChange: (e) => {
                            if (otpSent || otpVerified) {
                              setOtpSent(false);
                              setOtpVerified(false);
                              setOtp('');
                            }
                          }
                        })}
                        className={`w-full sm:flex-1 input-primary ${errors.mobile ? 'border-red-500' : ''} ${otpVerified ? 'border-green-500 bg-green-50' : ''}`}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                        disabled={otpVerified}
                      />
                      {!otpVerified && (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={isSendingOtp || !mobileValue || mobileValue.length < 10}
                          className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${isSendingOtp || !mobileValue || mobileValue.length < 10
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                        >
                          {isSendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Send OTP'}
                        </button>
                      )}
                      {otpVerified && (
                        <div className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          Verified
                        </div>
                      )}
                    </div>
                    {errors.mobile && (
                      <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
                    )}

                    {/* OTP Input Section */}
                    {otpSent && !otpVerified && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-3 bg-blue-50 p-4 rounded-lg border border-blue-200"
                      >
                        <label className="block text-sm font-medium text-blue-800 mb-2">
                          Enter OTP sent to your mobile
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="Enter 6-digit OTP"
                            maxLength={6}
                            className="w-full sm:flex-1 p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 transition text-center text-lg tracking-widest"
                          />
                          <button
                            type="button"
                            onClick={handleVerifyOtp}
                            disabled={otp.length < 6}
                            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-all ${otp.length < 6
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                          >
                            Verify
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.email')}
                    </label>
                    <input
                      {...register('email', {
                        validate: (value) => !value || validateEmail(value) || t('forms.validEmail')
                      })}
                      className={`input-primary ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="Enter email address (optional)"
                      type="email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.city')}
                    </label>
                    <input
                      {...register('city')}
                      className="input-primary"
                      placeholder="Enter your city"
                    />
                  </div>

                  {/* Applying As */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.applyingAs')} *
                    </label>
                    <select
                      {...register('applying_as', { required: t('forms.required') })}
                      className={`input-primary ${errors.applying_as ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select option</option>
                      <option value="Individual">Individual</option>
                      <option value="Proprietorship">Proprietorship</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Pvt Ltd / LLP">Pvt Ltd / LLP</option>
                    </select>
                    {errors.applying_as && (
                      <p className="text-red-500 text-sm mt-1">{errors.applying_as.message}</p>
                    )}
                  </div>

                  {/* Years of Experience */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('forms.experienceYears')} *
                    </label>
                    <input
                      {...register('experience_years', { required: t('forms.required') })}
                      className={`input-primary ${errors.experience_years ? 'border-red-500' : ''}`}
                      placeholder="Enter your experience"
                      type="text"
                    />
                    {errors.experience_years && (
                      <p className="text-red-500 text-sm mt-1">{errors.experience_years.message}</p>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('forms.details')}
                  </label>
                  <textarea
                    {...register('details')}
                    className="input-primary"
                    rows={4}
                    placeholder="Enter details here..."
                  />
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || !otpVerified}
                    className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${isSubmitting || !otpVerified
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                      }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/30" />
                        <span>Submitting...</span>
                      </div>
                    ) : !otpVerified ? (
                      'Verify Phone to Submit'
                    ) : (
                      t('forms.submit')
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-outline py-3 px-6"
                  >
                    Cancel
                  </button>
                </div>
                {!otpVerified && (
                  <p className="text-xs text-gray-500 text-center">
                    Please verify your phone number with OTP to submit the enquiry
                  </p>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EnquiryModal;